import { useLayoutEffect, useRef, useState } from 'react'

type SeasonToggleRebrandProps = {
  isCurrent: boolean
  onChange: (isCurrent: boolean) => void
}

// Note: Figma spec uses `#1D4321` for the dark green; nearest palette token is
// `forestDeep` (#08441B). `#FBF7F3` for the cream → `linen` (#F9F5F0).
const SeasonToggleRebrand = ({
  isCurrent,
  onChange
}: SeasonToggleRebrandProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const currentRef = useRef<HTMLButtonElement>(null)
  const allRef = useRef<HTMLButtonElement>(null)
  const [indicator, setIndicator] = useState({
    left: 0,
    width: 0,
    ready: false
  })

  useLayoutEffect(() => {
    const measure = () => {
      const btn = isCurrent ? currentRef.current : allRef.current
      if (btn) {
        setIndicator({
          left: btn.offsetLeft,
          width: btn.offsetWidth,
          ready: true
        })
      }
    }
    measure()
    const observer = new ResizeObserver(measure)
    if (containerRef.current) observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [isCurrent])

  return (
    <div
      ref={containerRef}
      role='radiogroup'
      aria-label='Filter by season'
      className='relative inline-flex w-full md:w-auto items-center gap-1 p-1 h-[60px] bg-white border border-forestDeep/30 rounded-lg'
    >
      <span
        aria-hidden
        className='absolute top-1 bottom-1 rounded bg-forestDeep transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]'
        style={{
          left: indicator.left,
          width: indicator.width,
          opacity: indicator.ready ? 1 : 0
        }}
      />

      <button
        ref={currentRef}
        type='button'
        role='radio'
        aria-checked={isCurrent}
        onClick={() => onChange(true)}
        className={`relative z-10 flex-1 flex items-center justify-center gap-2 px-3 h-full rounded text-base font-body-rebrand transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forestDeep focus-visible:ring-offset-1 ${
          isCurrent
            ? 'text-linen cursor-default pl-6 pr-6'
            : 'text-forestDeep cursor-pointer pr-6'
        }`}
      >
        <span className='relative flex h-2 w-2 items-center justify-center'>
          {isCurrent && (
            <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-limeBright opacity-75' />
          )}
          <span
            className={`relative inline-flex h-2 w-2 rounded-full transition-colors duration-200 ${
              isCurrent ? 'bg-limeBright' : 'bg-forestDeep/40'
            }`}
          />
        </span>
        Current
      </button>

      <button
        ref={allRef}
        type='button'
        role='radio'
        aria-checked={!isCurrent}
        onClick={() => onChange(false)}
        className={`relative z-10 flex-1 flex items-center justify-center px-3 h-full rounded text-base font-body-rebrand transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forestDeep focus-visible:ring-offset-1 ${
          !isCurrent
            ? 'text-linen cursor-default pl-6 pr-6'
            : 'text-forestDeep cursor-pointer pr-6'
        }`}
      >
        All time
      </button>
    </div>
  )
}

export default SeasonToggleRebrand
